import { useEffect, useState } from 'react';
import api, { API_BASE } from '@/lib/api';
import { PageHeader, LoadingShell } from '@/components/ui-bits';
import { MessageCircle, Save, Copy, Code } from 'lucide-react';
import { toast } from 'sonner';

export default function WebchatPage() {
  const [cfg, setCfg] = useState(null);
  const [snippet, setSnippet] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/webchat/config');
      setCfg(data.config);
      setSnippet(data.embed_snippet || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar webchat');
    }
  };
  useEffect(() => { load(); }, []);

  if (!cfg) return <LoadingShell />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/webchat/config', {
        enabled: cfg.enabled,
        title: cfg.title,
        welcome: cfg.welcome,
        primary_color: cfg.primary_color,
        require_email: cfg.require_email,
        require_phone: cfg.require_phone,
        offline_message: cfg.offline_message,
        auto_tag: cfg.auto_tag,
        hours_enabled: !!cfg.hours_enabled,
        hours_start: Number(cfg.hours_start ?? 9),
        hours_end: Number(cfg.hours_end ?? 18),
        hours_days: cfg.hours_days || [0, 1, 2, 3, 4],
        hours_timezone: cfg.hours_timezone || 'America/Mexico_City',
        allow_images: cfg.allow_images !== false,
      });
      setCfg(data.config);
      setSnippet(data.embed_snippet || '');
      toast.success('Webchat guardado');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(snippet);
    toast.success('Snippet copiado');
  };

  // Prefer same host API for embed preview
  const localSnippet = `<script src="${API_BASE}/public/webchat/widget.js" data-public-id="${cfg.public_id}" async></script>`;

  return (
    <div>
      <PageHeader
        title="Webchat"
        description="Widget embebible para tu sitio. Los mensajes llegan al Inbox omni como canal Webchat."
        actions={(
          <button type="button" className="btn-primary" disabled={saving} onClick={save}>
            <Save className="h-4 w-4" /> {saving ? 'Guardando…' : 'Guardar'}
          </button>
        )}
      />

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface p-5 space-y-3 text-sm">
          <h2 className="font-display font-semibold flex items-center gap-2">
            <MessageCircle className="h-4 w-4" /> Configuración
          </h2>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} />
            Activo
          </label>
          <label className="block text-xs">
            Título del widget
            <input className="input mt-1" value={cfg.title || ''} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} />
          </label>
          <label className="block text-xs">
            Mensaje de bienvenida
            <textarea className="input mt-1 min-h-[72px]" value={cfg.welcome || ''} onChange={(e) => setCfg({ ...cfg, welcome: e.target.value })} />
          </label>
          <label className="block text-xs">
            Color principal
            <div className="flex gap-2 mt-1">
              <input type="color" value={cfg.primary_color || '#0d9488'} onChange={(e) => setCfg({ ...cfg, primary_color: e.target.value })} className="h-10 w-12 rounded" />
              <input className="input" value={cfg.primary_color || ''} onChange={(e) => setCfg({ ...cfg, primary_color: e.target.value })} />
            </div>
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.require_email} onChange={(e) => setCfg({ ...cfg, require_email: e.target.checked })} />
            Exigir email al iniciar
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.require_phone} onChange={(e) => setCfg({ ...cfg, require_phone: e.target.checked })} />
            Exigir teléfono al iniciar
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={cfg.allow_images !== false} onChange={(e) => setCfg({ ...cfg, allow_images: e.target.checked })} />
            Permitir imágenes del visitante
          </label>
          <label className="block text-xs">
            Tag automático en leads
            <input className="input mt-1" value={cfg.auto_tag || ''} onChange={(e) => setCfg({ ...cfg, auto_tag: e.target.value })} />
          </label>

          <div className="border-t border-border pt-3 space-y-2">
            <label className="flex items-center gap-2 text-xs font-medium">
              <input type="checkbox" checked={!!cfg.hours_enabled} onChange={(e) => setCfg({ ...cfg, hours_enabled: e.target.checked })} />
              Horario de atención
            </label>
            {cfg.hours_enabled && (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs">Desde (h)
                    <input type="number" min={0} max={23} className="input mt-1" value={cfg.hours_start ?? 9} onChange={(e) => setCfg({ ...cfg, hours_start: Number(e.target.value) })} />
                  </label>
                  <label className="text-xs">Hasta (h)
                    <input type="number" min={0} max={23} className="input mt-1" value={cfg.hours_end ?? 18} onChange={(e) => setCfg({ ...cfg, hours_end: Number(e.target.value) })} />
                  </label>
                </div>
                <label className="text-xs block">Zona horaria
                  <input className="input mt-1" value={cfg.hours_timezone || 'America/Mexico_City'} onChange={(e) => setCfg({ ...cfg, hours_timezone: e.target.value })} />
                </label>
                <div className="flex flex-wrap gap-2 text-xs">
                  {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((lab, i) => {
                    const days = cfg.hours_days || [0, 1, 2, 3, 4];
                    const on = days.includes(i);
                    return (
                      <button
                        key={lab}
                        type="button"
                        className={`chip ${on ? 'bg-primary/15 text-primary' : ''}`}
                        onClick={() => {
                          const set = new Set(days);
                          if (set.has(i)) set.delete(i); else set.add(i);
                          setCfg({ ...cfg, hours_days: [...set].sort() });
                        }}
                      >
                        {lab}
                      </button>
                    );
                  })}
                </div>
                <label className="text-xs block">Mensaje fuera de horario
                  <textarea className="input mt-1 min-h-[60px]" value={cfg.offline_message || ''} onChange={(e) => setCfg({ ...cfg, offline_message: e.target.value })} />
                </label>
              </>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Public ID: <code className="font-mono">{cfg.public_id}</code>
          </div>
        </div>

        <div className="surface p-5 space-y-3">
          <h2 className="font-display font-semibold flex items-center gap-2">
            <Code className="h-4 w-4" /> Código embed
          </h2>
          <p className="text-xs text-muted-foreground">
            Pega este script antes de <code>&lt;/body&gt;</code> en tu web. Aparece un botón flotante de chat.
          </p>
          <pre className="text-[11px] font-mono bg-secondary/40 p-3 rounded-[10px] overflow-x-auto whitespace-pre-wrap break-all">
            {localSnippet}
          </pre>
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={() => { navigator.clipboard.writeText(localSnippet); toast.success('Copiado'); }}>
              <Copy className="h-4 w-4" /> Copiar snippet
            </button>
            {snippet && snippet !== localSnippet && (
              <button type="button" className="btn-ghost text-xs" onClick={copy}>Copiar (cdn prod)</button>
            )}
          </div>
          <div className="border-t border-border pt-3 text-xs text-muted-foreground space-y-1">
            <p>· Crea/encuentra lead por email o teléfono</p>
            <p>· Mensajes en Inbox con canal <b>webchat</b></p>
            <p>· Horario de atención + mensaje offline</p>
            <p>· Typing indicator (visitante ↔ agente)</p>
            <p>· Imágenes (máx ~1.5MB) si están habilitadas</p>
            <p>· Notificaciones del navegador (si el agente las autoriza)</p>
          </div>
        </div>
      </div>
    </div>
  );
}
